'use client';

import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import styles from './Home.module.css';
import { HomeSEO } from '@/seo/HomeSEO';
import { IconArrowRight, IconCheck, IconClipboard, IconDoc, IconHardHat, IconHeadset, IconLayers, IconShield, IconStar, IconTruck, IconWhatsApp } from '@/components/Icons/Icons';
import { SoundExperience } from '@/components/SoundExperience/SoundExperience';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import type { AccordionItem } from '@/components/Accordion/Accordion';
import { getProductsByCategory, products } from '@/data/products';
import { buildWhatsAppUrl } from '@/utils/whatsapp';

const BARIYERLI_BONDEX_SPECS = [
  { label: 'Çekirdek yoğunluk', value: '120 · 150 · 180 DNS rebonded süngerpan' },
  { label: 'Panel kalınlığı', value: '13 · 23 · 33 · 43 · 53 mm seçenekleri' },
  { label: 'NRC ses yutum', value: '0,60 ile 0,98 arası' },
  { label: 'Standart ebatlar', value: '100 x 100 cm plaka' },
  { label: 'Yangın sınıfı', value: 'TSE E sınıfı · TS EN 13501 1' },
  { label: 'Yüzey yapısı', value: 'Bondex gövde · ses bariyeri katmanı' },
  { label: 'Montaj yöntemi', value: 'Yapıştırma · karkas · kaplama altı' },
  { label: 'Ses yalıtımı', value: '≈ 25 ila 50 dB yaklaşık değer' },
  { label: 'Uygulama alanı', value: 'Konut · ofis · stüdyo · makine odası' },
  { label: 'Garanti', value: 'Kurumsal üretim garantisi' },
  { label: 'Teslimat', value: '1 gün stoktan sevkiyat' },
  { label: 'Minimum sipariş', value: 'Proje bazlı · ücretsiz teknik keşif' },
] as const;

const PARTNER_LOGOS = [
  { src: '/images/partner-logos/mercedes-sm.webp', alt: 'Mercedes-Benz' },
  { src: '/images/partner-logos/fibabanka-sm.webp', alt: 'Fibabanka' },
  { src: '/images/partner-logos/Borusan-sm.webp', alt: 'Borusan' },
  { src: '/images/partner-logos/EnerjiSA_logo-sm.webp', alt: 'EnerjiSA' },
  { src: '/images/partner-logos/Enpara.com_Logo-sm.webp', alt: 'Enpara' },
  { src: '/images/partner-logos/Finansbank_logo-sm.webp', alt: 'QNB Finansbank' },
  { src: '/images/partner-logos/remax-sm.webp', alt: 'RE/MAX' },
  { src: '/images/partner-logos/kobi-osb-sm.webp', alt: 'KOBİ OSB' },
  { src: '/images/partner-logos/Konya-organize-sanayi-sm.webp', alt: 'Konya Organize Sanayi' },
  { src: '/images/partner-logos/iveco-sm.webp', alt: 'IVECO' },
] as const;

const MODEL_SHOWCASE_CATEGORIES = [
  {
    title: 'Bondex Sünger',
    slugPrefix: 'bondex-sunger',
    promoTitle: 'Sessiz Mekanlar',
    promoSub: 'Konforlu Yaşamlar',
    promoDiscount: '%30 İndirim',
  },
  {
    title: 'Tek Bariyerli Bondex Sünger',
    slugPrefix: 'bariyerli-bondex-sunger',
    promoTitle: 'Daha Az Gürültü',
    promoSub: 'Daha Çok Konfor',
    promoDiscount: '%25 İndirim',
  },
  {
    title: 'Çift Bariyerli Bondex Sünger',
    slugPrefix: 'cift-bariyerli-bondex-sunger',
    promoTitle: 'Maksimum İzolasyon',
    promoSub: 'Premium Çözüm',
    promoDiscount: '%20 İndirim',
  },
] as const;

export function Home() {
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const heroRef = useRef<HTMLElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const bariyerliRef = useRef<HTMLDivElement | null>(null);
  const modelSliderRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [bariyerliVisible, setBariyerliVisible] = useState(false);

  useEffect(() => {
    // Check if mobile
    const mobile = window.innerWidth < 768 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setIsMobile(mobile);

    // Skip animations on mobile - make everything visible immediately
    if (mobile) {
      // Make all sections visible immediately on mobile
      sectionRefs.current.forEach((section) => {
        if (section) {
          section.classList.add(styles.visible);
        }
      });

      // Disable parallax on mobile
      if (heroRef.current) {
        const heroContent = heroRef.current.querySelector(`.${styles.heroContent}`);
        const heroVisual = heroRef.current.querySelector(`.${styles.heroVisual}`);
        if (heroContent) {
          (heroContent as HTMLElement).style.transform = 'translate3d(0, 0, 0)';
        }
        if (heroVisual) {
          (heroVisual as HTMLElement).style.transform = 'translate3d(0, 0, 0)';
        }
      }

      return;
    }

    // Desktop animations only
    const observers: IntersectionObserver[] = [];
    let rafId: number | null = null;
    let lastScrollY = 0;

    sectionRefs.current.forEach((section) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add(styles.visible);
              // Disconnect after animation to save performance
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.05,
          rootMargin: '0px 0px -100px 0px',
        }
      );

      observer.observe(section);
      observers.push(observer);
    });

    // Optimized parallax with requestAnimationFrame and throttling
    const handleScroll = () => {
      if (rafId) return; // Skip if already scheduled

      rafId = requestAnimationFrame(() => {
        const scrolled = window.scrollY || window.pageYOffset || 0;

        // Only update if scroll changed significantly (throttle)
        if (Math.abs(scrolled - lastScrollY) < 2) {
          rafId = null;
          return;
        }

        lastScrollY = scrolled;

        if (!heroRef.current) {
          rafId = null;
          return;
        }

        const heroContent = heroRef.current.querySelector(`.${styles.heroContent}`);
        const heroVisual = heroRef.current.querySelector(`.${styles.heroVisual}`);

        if (heroContent && scrolled < window.innerHeight) {
          (heroContent as HTMLElement).style.transform = `translate3d(0, ${scrolled * 0.15}px, 0)`;
        }
        if (heroVisual && scrolled < window.innerHeight) {
          (heroVisual as HTMLElement).style.transform = `translate3d(0, ${scrolled * 0.1}px, 0)`;
        }

        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observers.forEach((observer) => observer.disconnect());
      window.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    const el = bariyerliRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setBariyerliVisible(true); io.disconnect(); } },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const quoteUrl = buildWhatsAppUrl('Merhaba, Bariyerli Bondex Sünger ürünleriniz için fiyat teklifi almak istiyorum.');
  const bondex = getProductsByCategory('Bondex Sünger').slice(0, 3);
  const bariyerli = getProductsByCategory('Bariyerli Bondex').slice(0, 3);
  const heroBondex = bondex[0];
  const heroBariyerli = bariyerli[0];

  const homeFAQs: AccordionItem[] = [
    {
      question: 'Bariyerli Bondex Sünger nedir?',
      answer: 'Bariyerli Bondex Sünger; Bondex Sünger ile ses bariyeri katmanının birleştiği, hava kaynaklı ses geçişini azaltmaya odaklı kompozit bir ses yalıtım çözümüdür. Bariyer katmanı sayesinde klasik Bondex Sünger\'e göre daha yüksek izolasyon performansı sağlar. Duvar, kapı ve yüksek izolasyon gereken projelerde tercih edilir. Bariyerli Bondex Sünger fiyatları kalınlık, metraj ve proje adedine göre değişir.',
    },
    {
      question: 'Bariyerli Bondex Sünger ile Bondex Sünger arasındaki fark nedir?',
      answer: 'Bondex Sünger; rebonded süngerpan yapısıyla ses yalıtımı ve titreşim sönümleme için kullanılan yüksek yoğunluklu bir sünger türüdür. Bariyerli Bondex Sünger ise Bondex Sünger\'e ek olarak ses bariyeri katmanı içeren kompozit bir çözümdür. Bariyer katmanı sayesinde hava kaynaklı ses geçişini daha etkili şekilde azaltır. Daha yüksek izolasyon hedeflerinde Bariyerli Bondex Sünger tercih edilir.',
    },
    {
      question: 'Bariyerli Bondex Sünger mi, Çift Bariyerli Bondex Sünger mi tercih edilmeli?',
      answer: 'Bariyerli Bondex Sünger; tek bariyer katmanı ile yüksek izolasyon gereken projeler için idealdir. Çift Bariyerli Bondex Sünger ise çift bariyer katmanı ile maksimum izolasyon performansı sunar. Proje ihtiyacınıza göre: standart yüksek izolasyon için Bariyerli Bondex Sünger, maksimum izolasyon gereken kritik projeler için Çift Bariyerli Bondex Sünger tercih edilir. Doğru seçim için proje detaylarınızı paylaşın, size en uygun çözümü önerelim.',
    },
    {
      question: 'Bariyerli Bondex Sünger fiyatları ne kadar?',
      answer: 'Bariyerli Bondex Sünger fiyatları metrekare (m²) başına 200-310 TL arası değişir. Kalınlığa göre: 23mm 200-230 TL/m², 33mm 240-270 TL/m², 43mm 280-310 TL/m², 53mm 300-330 TL/m². Bondex Sünger 130-260 TL/m², Çift Bariyerli Bondex Sünger 350-450 TL/m² arası. Fiyatlar metraj ve proje adedine göre değişir. Bariyerli Bondex Sünger için hızlı teklif almak için WhatsApp üzerinden iletişime geçebilirsiniz.',
    },
    {
      question: 'Bariyerli Bondex Sünger nasıl uygulanır?',
      answer: 'Bariyerli Bondex Sünger uygulaması yüzeye ve proje detayına göre değişir. Çoğu senaryoda uygun yapıştırıcı ile duvar, kapı veya zemin yüzeyine sabitlenir. Bariyer katmanının doğru yönde yerleştirilmesi ve kesim/ek yerlerinin düzgün yapılması performansı etkiler. Proje bazlı uygulama önerilir. Uzman ekibimiz size Bariyerli Bondex Sünger uygulama desteği sağlayabilir.',
    },
    {
      question: 'Bariyerli Bondex Sünger hangi projelerde kullanılır?',
      answer: 'Bariyerli Bondex Sünger; stüdyo ve kayıt odası ses yalıtımı, ofis ve yaşam alanları izolasyonu, duvar ve kapı ses yalıtımı, yüksek izolasyon gereken endüstriyel projeler ve proje bazlı özel çözümlerde kullanılır. Bariyer katmanı sayesinde hava kaynaklı ses geçişini etkili şekilde azaltır. Türkiye geneline hızlı teslimat ile projelerinizde aksamaya yer vermiyoruz.',
    },
  ];

  const whyCards = [
    {
      title: 'Uzman Kadro',
      text: 'Ses yalıtımı projelerinizde teknik ekibimizle yanınızdayız. Doğru kalınlık ve malzeme seçimini tecrübeyle netleştiriyoruz.',
      icon: IconHardHat,
    },
    {
      title: 'Üreticiden Tedarik',
      text: 'Bariyerli Bondex Sünger’i üreticiden, ara toptancı olmadan temin edin. Kalite kontrolü ve fiyat avantajı doğrudan üretimden gelir.',
      icon: IconShield,
    },
    {
      title: '11 Yıllık Üretim Tecrübesi',
      text: '11 yıllık üretim tecrübemizle farklı proje tiplerinde doğru malzeme ve uygulama detaylarını netleştiriyoruz. Sahadan gelen bilgiyle uzun ömürlü ve güvenilir çözümler sunuyoruz.',
      icon: IconLayers,
    },
    {
      title: 'Ücretsiz Teklif',
      text: 'Metraj ve yüzey bilgilerinizi paylaşın. Aynı gün içinde şeffaf fiyat teklifi sunuyor, hiçbir ön ücret talep etmiyoruz.',
      icon: IconClipboard,
    },
    {
      title: 'Türkiye Geneli Teslimat',
      text: 'İstanbul’dan Anadolu’ya hızlı ve güvenli sevkiyat ile projelerinizde aksamaya yer vermiyoruz. Stoktan planlı teslimat yapıyoruz.',
      icon: IconTruck,
    },
    {
      title: '7/24 WhatsApp Destek',
      text: 'Sipariş, stok ve teknik sorularınız için WhatsApp üzerinden günün her saati ulaşın. Kesintisiz iletişim sağlıyoruz.',
      icon: IconHeadset,
    },
  ];

  const modelShowcaseGroups = MODEL_SHOWCASE_CATEGORIES.map((group) => {
    const items = products
      .filter((p) => p.slug.startsWith(group.slugPrefix))
      .sort((a, b) => {
        if (a.thicknessMm == null && b.thicknessMm == null) return 0;
        if (a.thicknessMm == null) return -1;
        if (b.thicknessMm == null) return 1;
        return a.thicknessMm - b.thicknessMm;
      });
    return { ...group, items };
  });

  const formatPrice = (price?: number) => {
    if (!price) return 'Fiyat için teklif alın';
    return `₺${price.toLocaleString('tr-TR')} /m²`;
  };

  const handleModelSlide = (title: string, direction: 'left' | 'right') => {
    const sliderEl = modelSliderRefs.current[title];
    if (!sliderEl) return;
    const cardEl = sliderEl.querySelector<HTMLElement>(`.${styles.modelShowcaseCard}`);
    const cardWidth = cardEl ? cardEl.offsetWidth : sliderEl.clientWidth / 4;
    const gap = 12;
    const scrollStep = cardWidth + gap;
    sliderEl.scrollBy({
      left: direction === 'right' ? scrollStep : -scrollStep,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <HomeSEO />

      <div className={styles.homePage}>
      <section ref={heroRef} className={styles.hero}>
        <div className={styles.heroMedia} aria-hidden="true">
          <video
            className={styles.heroVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/images/header-gorsel.webp"
          >
            <source src="/images/slider-video.mp4" type="video/mp4" />
          </video>
        </div>

        <div className={styles.heroContent}>
          <div className="container">
            <div className={styles.heroCopy}>
              <div className={styles.kicker}>
                <span className={styles.kickerDot}></span>
                Kurumsal tedarik • Hızlı teklif • Türkiye geneli
              </div>
              <h1 className={styles.h1}>
                Bariyerli <span className={styles.titleBrand}>Bondex</span> Sünger
              </h1>
              <p className={styles.heroTagline}>
                <strong>Bariyerli Bondex Sünger</strong> Türkiye'nin üreticisi olmaktan gurur duyuyoruz
              </p>
              <p className={styles.lead}>
                <strong>Bariyerli Bondex Sünger</strong> ile ses yalıtımı projelerinizde üstün performans.
                Bariyer katmanlı kompozit yapısıyla hava kaynaklı ses geçişini azaltan <strong>Bariyerli Bondex Sünger</strong> çözümlerini keşfedin.
                Klasik <Link to="/bondex-sunger" rel="follow" className={styles.leadLink}>
                  <strong>Bondex Sünger</strong>
                </Link>{' '}
                ve maksimum izolasyon için{' '}
                <Link to="/cift-bariyerli-bondex-sunger" rel="follow" className={styles.leadLink}>
                  <strong>Çift Bariyerli Bondex Sünger</strong>
                </Link>{' '}
                seçenekleriyle birlikte karşılaştırın; ürünleri inceleyin, sepete ekleyin ve{' '}
                <strong>Bariyerli Bondex Sünger fiyat teklifi</strong>ni WhatsApp'tan hızlıca alın.
              </p>
              <div className={styles.heroStats}>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>500+</div>
                  <div className={styles.statLabel}>Başarılı Proje</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>%100</div>
                  <div className={styles.statLabel}>Müşteri Memnuniyeti</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>24/7</div>
                  <div className={styles.statLabel}>Destek</div>
                </div>
              </div>
              <div className={styles.heroCtas}>
                <Link className={`btn btnPrimary ${styles.ctaPrimary}`} to="/urunler">
                  Ürünleri Keşfet
                  <IconArrowRight />
                </Link>
                <a
                  className={`btn ${styles.ctaSecondary}`}
                  href={quoteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconWhatsApp />
                  Hızlı Teklif Al
                </a>
              </div>
            </div>
          </div>
        </div>

      </section>

      <section className={styles.partnerLogos} aria-label="Referans markalar">
        <div className={styles.partnerLogosTrack}>
          {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map((logo, index) => (
            <div key={`${logo.alt}-${index}`} className={styles.partnerLogoItem}>
              <img src={logo.src} alt={logo.alt} loading="lazy" decoding="async" />
            </div>
          ))}
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.featuredSection} ${styles.fadeInUp} ${styles.visible}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>Kategoriler</div>
            <h2 className={styles.sectionTitle}>
              Bariyerli <span className={styles.titleBrand}>Bondex</span> Sünger, Bondex Sünger ve Çift{' '}
              <span className={styles.titleBrand}>Bariyerli</span> Bondex Sünger Kategorileri
            </h2>
            <p className={styles.sectionSubtitle}>
              <strong>Bariyerli Bondex Sünger</strong> ana ürünümüzle birlikte <strong>Bondex Sünger</strong> ve <strong>Çift Bariyerli Bondex Sünger</strong> seçenekleriyle proje ihtiyacınıza uygun çözümleri keşfedin
            </p>
          </div>
          <div className={styles.featuredGrid} aria-label="Öne çıkan kategoriler">
            <article className={`${styles.featuredCard} ${styles.staggerItem}`} style={{ animationDelay: '0.1s' }}>
              <div className={styles.featuredMedia} aria-hidden="true">
                {heroBondex ? <img className={styles.featuredImg} src={heroBondex.imagePath} alt={heroBondex.name} loading="lazy" /> : null}
                <div className={styles.featuredOverlay}></div>
                <div className={styles.featuredBadge}>Popüler</div>
              </div>
              <div className={styles.featuredBody}>
                <div className={styles.featuredCategory}>Bondex Sünger</div>
                <h3 className={styles.featuredTitle}>Bondex Sünger: Ses Yalıtımı ve Titreşim Sönümleme</h3>
                <p className={styles.featuredText}>
                  <strong>Bondex sünger</strong>; süngerpan (rebonded) yapısıyla ses yalıtımı ve titreşim sönümleme projelerinde tercih edilir.
                  10mm, 20mm, 30mm, 40mm, 50mm kalınlık seçenekleriyle proje ihtiyacına göre seçebilirsiniz.
                  <strong>Bondex sünger</strong> fiyatları için WhatsApp'tan hızlı teklif alın.
                </p>
                <Link className={styles.featuredLink} to="/bondex-sunger" rel="follow">
                  Bondex Sünger Ürünleri
                  <IconArrowRight />
                </Link>
              </div>
            </article>

            <article className={`${styles.featuredCard} ${styles.staggerItem}`} style={{ animationDelay: '0.3s' }}>
              <div className={styles.featuredMedia} aria-hidden="true">
                {heroBariyerli ? (
                  <img className={styles.featuredImg} src={heroBariyerli.imagePath} alt={heroBariyerli.name} loading="lazy" />
                ) : null}
                <div className={styles.featuredOverlay}></div>
                <div className={styles.featuredBadge}>Premium</div>
              </div>
              <div className={styles.featuredBody}>
                <div className={styles.featuredCategory}>Bariyerli Bondex Sünger</div>
                <h3 className={styles.featuredTitle}>Bariyerli Bondex Sünger: Yüksek Performanslı Ses Yalıtım Çözümü</h3>
                <p className={styles.featuredText}>
                  <strong>Bariyerli Bondex Sünger</strong>; Bondex Sünger ile ses bariyeri katmanının birleştiği, hava kaynaklı ses geçişini azaltmaya odaklı kompozit bir çözümdür.
                  23mm, 33mm, 43mm, 53mm kalınlık seçenekleriyle duvar, kapı ve yüksek izolasyon gereken projelerde tercih edilir.
                  Klasik <strong>Bondex Sünger</strong>'e göre daha yüksek izolasyon performansı sunan <strong>Bariyerli Bondex Sünger</strong> fiyatları için WhatsApp'tan hızlı teklif alın.
                </p>
                <Link className={styles.featuredLink} to="/bariyerli-bondex-sunger" rel="follow">
                  Bariyerli Bondex Sünger Ürünleri
                  <IconArrowRight />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.productsSection} ${styles.fadeInUp} ${styles.visible}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Bondex Sünger Modelleri ve Fiyatları</h2>
            <p className={styles.sectionSubtitle}>
              <strong>Bondex sünger</strong> modellerimizi inceleyin, kalınlık seçeneklerini karşılaştırın ve fiyat teklifi alın
            </p>
            <Link to="/bondex-sunger" className={styles.sectionLink} rel="follow">
              Tüm Bondex Sünger Ürünleri <IconArrowRight />
            </Link>
          </div>
          <div className={styles.productsInfo}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>Bondex Sünger Özellikleri</h3>
              <ul className={styles.infoList}>
                <li>Yüksek yoğunluklu rebonded süngerpan yapısı</li>
                <li>10mm, 20mm, 30mm, 40mm, 50mm kalınlık seçenekleri</li>
                <li>Ses yalıtımı ve titreşim sönümleme</li>
                <li>Kolay kesim ve uygulama</li>
                <li>Proje bazlı özel ölçü ve montaj</li>
              </ul>
            </div>
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>Kullanım Alanları</h3>
              <ul className={styles.infoList}>
                <li>Stüdyo ve müzik odası ses yalıtımı</li>
                <li>Jeneratör odası ve makine dairesi</li>
                <li>Kapı ve duvar detaylarında darbe azaltma</li>
                <li>Zemin altı şap ve kaplama altı uygulamalar</li>
                <li>Endüstriyel hacimlerde titreşim sönümleme</li>
              </ul>
            </div>
          </div>
          <div className={styles.grid}>
            {bondex.map((p) => (
              <ProductCard key={p.slug} product={p} variant="catalog" />
            ))}
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.productsSection} ${styles.fadeInUp} ${styles.visible}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Bariyerli Bondex Sünger Modelleri ve Fiyatları</h2>
            <p className={styles.sectionSubtitle}>
              <strong>Bariyerli Bondex Sünger</strong> modellerimizi inceleyin, kalınlık seçeneklerini karşılaştırın. Bondex Sünger ve Çift Bariyerli Bondex Sünger ile birlikte değerlendirin ve <strong>Bariyerli Bondex Sünger</strong> fiyat teklifi alın
            </p>
            <Link to="/bariyerli-bondex-sunger" className={styles.sectionLink} rel="follow">
              Tüm Bariyerli Bondex Sünger Ürünleri <IconArrowRight />
            </Link>
          </div>
          <div className={styles.productsInfo}>
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>Bariyerli Bondex Sünger Avantajları</h3>
              <ul className={styles.infoList}>
                <li>Bariyer katmanı ile Bondex Sünger'e göre daha yüksek izolasyon</li>
                <li>23mm, 33mm, 43mm, 53mm kalınlık seçenekleri</li>
                <li>Hava kaynaklı ses geçişini etkili şekilde azaltma</li>
                <li>Duvar ve kapı detaylarında ideal performans</li>
                <li>Çift Bariyerli Bondex Sünger'e göre daha ekonomik çözüm</li>
              </ul>
            </div>
            <div className={styles.infoCard}>
              <h3 className={styles.infoTitle}>Bariyerli Bondex Sünger Kullanım Alanları</h3>
              <ul className={styles.infoList}>
                <li>Yüksek izolasyon gereken projeler (Bondex Sünger yetersiz kaldığında)</li>
                <li>Duvar ve kapı ses yalıtımı</li>
                <li>Stüdyo ve kayıt odası uygulamaları</li>
                <li>Ofis ve yaşam alanları izolasyonu</li>
                <li>Çift Bariyerli Bondex Sünger'e alternatif ekonomik çözüm</li>
              </ul>
            </div>
          </div>
          <div className={styles.grid}>
            {bariyerli.map((p) => (
              <ProductCard key={p.slug} product={p} variant="catalog" />
            ))}
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.valuesSection} ${styles.fadeInUp} ${styles.visible}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>Değerlerimiz</div>
            <h2 className={styles.sectionTitle}>Neden Bizi Tercih Etmelisiniz?</h2>
            <p className={styles.sectionSubtitle}>
              İnşaat sektöründe ses yalıtımı ve titreşim sönümleme çözümleri için güvenilir ortağınız
            </p>
          </div>

          <div className={styles.valueGrid} aria-label="Değer önerileri">
            <article className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <div className={styles.valueIcon}>
                  <IconCheck />
                </div>
              </div>
              <h3 className={styles.valueH3}>Kurumsal Süreç</h3>
              <p className={styles.valueText}>
                Profesyonel iletişim, hızlı yanıt ve güvenilir tedarik zinciri ile projelerinizde yanınızdayız.
              </p>
            </article>
            <article className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <div className={styles.valueIcon}>
                  <IconWhatsApp />
                </div>
              </div>
              <h3 className={styles.valueH3}>Hızlı Teslimat</h3>
              <p className={styles.valueText}>
                Türkiye geneline hızlı ve güvenli teslimat ile projelerinizde aksamaya yer vermiyoruz.
              </p>
            </article>
            <article className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <div className={styles.valueIcon}>
                  <IconStar />
                </div>
              </div>
              <h3 className={styles.valueH3}>Kalite Garantisi</h3>
              <p className={styles.valueText}>
                Yüksek yoğunluklu rebonded süngerpan ve performans/kalınlık dengesi için güçlü seçim.
              </p>
            </article>
            <article className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <div className={styles.valueIcon}>
                  <IconDoc />
                </div>
              </div>
              <h3 className={styles.valueH3}>Uzman Destek</h3>
              <p className={styles.valueText}>
                WhatsApp'tan hızlıca fiyat ve teslimat bilgisi alın, proje için doğru ürünü seçmenize yardımcı olalım.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        ref={(el) => { sectionRefs.current[0] = el; }}
        className={`${styles.section} ${styles.whySection} ${styles.fadeInUp} ${isMobile ? styles.visible : ''}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>Neden Biz?</div>
            <h2 className={styles.sectionTitle}>
              Neden <span className={styles.titleBrand}>Echopan</span> Bariyerli Bondex Süngerlerini Tercih{' '}
              <span className={styles.titleBrand}>Etmelisiniz</span>?
            </h2>
            <p className={styles.sectionSubtitle}>
              Ses yalıtımı ve titreşim sönümleme projelerinizde yanınızdayız
            </p>
          </div>
          <div className={styles.whyGrid} aria-label="Neden biz">
            {whyCards.map((card, index) => {
              const Icon = card.icon;
              const isDarkCard =
                card.title === 'Uzman Kadro' ||
                card.title === '11 Yıllık Üretim Tecrübesi' ||
                card.title === 'Ücretsiz Teklif' ||
                card.title === '7/24 WhatsApp Destek';
              return (
                <article
                  key={card.title}
                  className={`${styles.whyCard} ${isDarkCard ? styles.whyCardDark : ''} ${styles.staggerItem}`}
                  style={{ animationDelay: `${0.08 * (index + 1)}s` }}
                >
                  <div className={styles.whyIconWrapper}>
                    <div className={styles.whyIcon}>
                      <Icon />
                    </div>
                  </div>
                  <h3 className={styles.whyTitle}>{card.title}</h3>
                  <p className={styles.whyText}>{card.text}</p>
                  <div className={styles.whyLink}>
                    <a href={quoteUrl} target="_blank" rel="noopener noreferrer">
                      Hemen Teklif Al <IconArrowRight />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section
        ref={(el) => { sectionRefs.current[1] = el; }}
        className={`${styles.section} ${styles.specsSection} ${styles.fadeInUp} ${isMobile ? styles.visible : ''}`}
        aria-labelledby="bariyerli-bondex-specs-title"
      >
        <div className="container">
          <div className={styles.specsIntro}>
            <h2 id="bariyerli-bondex-specs-title" className={styles.specsTitle}>
              Bariyerli <span className={styles.titleBrand}>Bondex</span> Sünger Özellikleri
            </h2>
            <p className={styles.specsDesc}>
              Teknik değerler proje tipine göre netleştirilir. Duvar, kapı ve tavan uygulamalarında ses bariyeri katmanı hava kaynaklı gürültüyü hedefler.
            </p>
          </div>
          <div className={styles.specsGrid} role="group" aria-label="Bariyerli Bondex sünger teknik özellikleri">
            {BARIYERLI_BONDEX_SPECS.map((spec) => (
              <div key={spec.label} className={styles.specsCell}>
                <div className={styles.specsLabel}>{spec.label}</div>
                <div className={styles.specsValue}>{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements — Ella theme layout */}
      <section className={`${styles.section} ${styles.achieveSection}`}>
        <div className={styles.achieveInner}>
          <h2 className={styles.achieveHeading}>Elde Ettiğimiz Başarılar</h2>
          <p className={styles.achieveSubtext}>
            Müşterilerimize en iyi deneyimi sunmak için her gün daha fazla çalışıyoruz
          </p>

          <div className={styles.achieveRow}>
            <div className={styles.achieveStat}>
              <div className={styles.achieveCircle}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M6 9V2h12v7" /><path d="M4 22h16" /><path d="M10 22v-4a2 2 0 0 1 4 0v4" /><path d="M12 9v4" /><circle cx="12" cy="15" r="2" />
                </svg>
              </div>
              <div className={styles.achieveStatText}>
                <div className={styles.achieveLabel}>Tamamlanan Proje</div>
                <div className={styles.achieveNumber}>5.000+</div>
              </div>
            </div>
            <div className={styles.achieveStat}>
              <div className={styles.achieveCircle}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className={styles.achieveStatText}>
                <div className={styles.achieveLabel}>Mutlu Müşteri</div>
                <div className={styles.achieveNumber}>4.500+</div>
              </div>
            </div>
            <div className={styles.achieveStat}>
              <div className={styles.achieveCircle}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <div className={styles.achieveStatText}>
                <div className={styles.achieveLabel}>Olumlu Değerlendirme</div>
                <div className={styles.achieveNumber}>1.200+</div>
              </div>
            </div>
          </div>

          <div className={styles.achieveCards}>
            <article className={styles.achieveCardGreen}>
              <img
                className={styles.achieveBadgeImg}
                src="/images/achievements/elite-badge.svg"
                alt=""
                width={180}
                height={140}
                loading="lazy"
                decoding="async"
              />
              <p className={styles.achieveCardTag}>Echopan Üretici</p>
              <h3 className={styles.achieveCardTitle}>11 Yıldır Üretimden Sahaya</h3>
              <p className={styles.achieveCardNote}>Ara toptancı olmadan fabrikadan projenize doğrudan tedarik</p>
            </article>
            <article className={styles.achieveCardGold}>
              <img
                className={styles.achieveBadgeImg}
                src="/images/achievements/top-rated-badge.svg"
                alt=""
                width={200}
                height={150}
                loading="lazy"
                decoding="async"
              />
              <h3 className={styles.achieveCardTitleDark}>Sektörde En Çok Tercih Edilen Marka</h3>
              <p className={styles.achieveCardNoteDark}>Stüdyo, ofis ve konut projelerinde ilk tercih</p>
            </article>
          </div>
        </div>
      </section>

      <SoundExperience />

      <section className={`${styles.section} ${styles.modelShowcaseSection}`}>
        <div className="container">
          {modelShowcaseGroups.map((group) => (
            <div key={group.title} className={styles.modelShowcaseGroup}>
              <h2 className={styles.modelShowcaseTitle}>{group.title}</h2>
              <div className={styles.modelShowcaseRow}>
                <aside className={styles.modelPromoCard}>
                  <h3 className={styles.modelPromoTitle}>{group.promoTitle}</h3>
                  <p className={styles.modelPromoSub}>{group.promoSub}</p>
                  <p className={styles.modelPromoDiscount}>{group.promoDiscount}</p>
                  <div className={styles.modelPromoBadges}>
                    <span>Kolay Sipariş</span>
                    <span>Hızlı Teslimat</span>
                    <span>Kolay Uygulama</span>
                  </div>
                </aside>

                <div className={styles.modelShowcaseSliderWrap}>
                  <button
                    type="button"
                    className={`${styles.modelArrow} ${styles.modelArrowLeft}`}
                    onClick={() => handleModelSlide(group.title, 'left')}
                    aria-label={`${group.title} sol kaydır`}
                  >
                    ‹
                  </button>
                  <div
                    ref={(el) => { modelSliderRefs.current[group.title] = el; }}
                    className={styles.modelShowcaseSlider}
                  >
                    {group.items.map((item) => (
                      <article key={item.slug} className={styles.modelShowcaseCard}>
                        <Link to={`/${item.slug}`} className={styles.modelShowcaseImageLink}>
                          <img
                            src={item.imagePath}
                            alt={item.name}
                            loading="lazy"
                            decoding="async"
                            className={styles.modelShowcaseImage}
                          />
                        </Link>
                        <div className={styles.modelShowcaseBody}>
                          <h3 className={styles.modelShowcaseName}>{item.name}</h3>
                          <div className={styles.modelShowcaseMeta}>
                            {formatPrice(item.priceTRY)}
                          </div>
                          <div className={styles.modelShowcaseActions}>
                            <Link to={`/${item.slug}`} className={styles.modelShowcaseBtn}>
                              Ürünü İncele
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={`${styles.modelArrow} ${styles.modelArrowRight}`}
                    onClick={() => handleModelSlide(group.title, 'right')}
                    aria-label={`${group.title} sağ kaydır`}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Performance Bento Grid — Ella clone */}
      <section className={`${styles.section} ${styles.perfSection}`}>
        <div className={styles.perfInner}>
          <div className={styles.perfGrid}>
            {/* Top-left: Score card */}
            <div className={styles.perfScoreCard}>
              <div className={styles.perfScoreVisual}>
                <div className={styles.perfMainScore}>
                  <svg className={styles.perfArc} viewBox="0 0 120 120" aria-hidden="true">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#f0f0f0" strokeWidth="5" />
                    <circle cx="60" cy="60" r="52" fill="none" stroke="#ff4719" strokeWidth="5"
                      strokeLinecap="round" strokeDasharray="280 47" />
                  </svg>
                  <svg className={`${styles.perfArc} ${styles.perfArcArrow}`} viewBox="0 0 120 120" aria-hidden="true">
                    <defs>
                      <marker id="arrowTip" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                        <circle cx="3" cy="3" r="3" fill="#ff4719" />
                      </marker>
                    </defs>
                    <circle cx="60" cy="60" r="52" fill="none" stroke="transparent" strokeWidth="5"
                      strokeDasharray="8 319" markerEnd="url(#arrowTip)" />
                  </svg>
                  <span className={styles.perfMainLetter}>A</span>
                </div>
                <div className={styles.perfScoreStats}>
                  <div className={styles.perfScoreItem}>
                    <div className={styles.perfMiniWrap}>
                      <svg className={styles.perfMiniArc} viewBox="0 0 48 48" aria-hidden="true">
                        <circle cx="24" cy="24" r="20" fill="none" stroke="#f0f0f0" strokeWidth="3" />
                        <circle cx="24" cy="24" r="20" fill="none" stroke="#ff4719" strokeWidth="3"
                          strokeLinecap="round" strokeDasharray="110 16" />
                      </svg>
                      <svg className={`${styles.perfMiniArc} ${styles.perfMiniArrow}`} viewBox="0 0 48 48" aria-hidden="true">
                        <circle cx="24" cy="24" r="20" fill="none" stroke="transparent" strokeWidth="3"
                          strokeDasharray="4 122" markerEnd="url(#arrowTip)" />
                      </svg>
                    </div>
                    <div>
                      <div className={styles.perfScoreLabel}>Ses Yalıtımı</div>
                      <div className={styles.perfScoreVal}>98%</div>
                    </div>
                  </div>
                  <div className={styles.perfScoreItem}>
                    <div className={styles.perfMiniWrap}>
                      <svg className={styles.perfMiniArc} viewBox="0 0 48 48" aria-hidden="true">
                        <circle cx="24" cy="24" r="20" fill="none" stroke="#f0f0f0" strokeWidth="3" />
                        <circle cx="24" cy="24" r="20" fill="none" stroke="#ff4719" strokeWidth="3"
                          strokeLinecap="round" strokeDasharray="108 18" />
                      </svg>
                      <svg className={`${styles.perfMiniArc} ${styles.perfMiniArrow}`} viewBox="0 0 48 48" aria-hidden="true">
                        <circle cx="24" cy="24" r="20" fill="none" stroke="transparent" strokeWidth="3"
                          strokeDasharray="4 122" markerEnd="url(#arrowTip)" />
                      </svg>
                    </div>
                    <div>
                      <div className={styles.perfScoreLabel}>Dayanıklılık</div>
                      <div className={styles.perfScoreVal}>96%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top-right: Orange card */}
            <div className={styles.perfOrangeCard}>
              <h3 className={styles.perfOrangeTitle}>Üstün Performans</h3>
              <p className={styles.perfOrangeSub}>Maksimum ses yalıtımı</p>
              <p className={styles.perfOrangeText}>
                Bariyerli Bondex Sünger ile duvar, kapı ve tavan uygulamalarında hava kaynaklı gürültüyü hedefe yönelik azaltın
              </p>
            </div>

            {/* Bottom-left: Green card */}
            <div className={styles.perfGreenCard}>
              <h3 className={styles.perfGreenTitle}>Proje Bazlı Çözüm</h3>
              <p className={styles.perfGreenText}>
                Mekânınıza özel kalınlık, yoğunluk ve montaj planı ile en verimli sonuca ulaşın. Ücretsiz teknik keşif ile projenizi birlikte planlayalım.
              </p>
            </div>

            {/* Bottom-right: Chart card */}
            <div className={styles.perfChartCard}>
              <div className={styles.perfChartHeader}>
                <span className={styles.perfChartLabel}>Müşteri Memnuniyeti</span>
                <span className={styles.perfChartBadge}>+21.6%</span>
              </div>
              <svg className={styles.perfChartSvg} viewBox="0 0 400 160" preserveAspectRatio="none" aria-hidden="true">
                <defs>
                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e84e36" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#e84e36" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path className={styles.perfChartArea} d="M0,140 L50,145 L100,120 L150,130 L200,95 L250,110 L300,75 L350,85 L400,15 L400,160 L0,160 Z" fill="url(#chartGrad)" />
                <path className={styles.perfChartLine} d="M0,140 L50,145 L100,120 L150,130 L200,95 L250,110 L300,75 L350,85 L400,15" fill="none" stroke="#ff4719" strokeWidth="2.5" strokeLinejoin="round" />
                <circle cx="0" cy="140" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="50" cy="145" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="100" cy="120" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="150" cy="130" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="200" cy="95" r="5" fill="#ff4719" />
                <circle cx="250" cy="110" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="300" cy="75" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle cx="350" cy="85" r="4" fill="#fff" stroke="#ff4719" strokeWidth="2" />
                <circle className={styles.perfChartDot} cx="400" cy="15" r="6" fill="#ff4719" />
              </svg>
              <div className={styles.perfChartMonths}>
                <span>Oca</span><span>Şub</span><span>Mar</span><span>Nis</span><span>May</span><span>Haz</span><span>Tem</span><span>Ağu</span><span>Eyl</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={(el) => { sectionRefs.current[2] = el; }}
        className={`${styles.section} ${styles.productsSection} ${styles.bariyerliSection} ${styles.fadeInUp} ${isMobile ? styles.visible : ''}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <h2 className={`${styles.sectionTitle} ${styles.bariyerliSectionTitle}`}>Bariyerli Bondex Modelleri</h2>
          </div>
          <div ref={bariyerliRef} className={`${styles.bariyerliCards} ${bariyerliVisible ? styles.bariyerliVisible : ''}`}>
            {[
              { slug: 'bondex-sunger', title: 'Bondex Sünger', desc: 'Rebonded süngerpan yapısıyla temel ses yalıtımı ve titreşim sönümleme ihtiyaçları için ekonomik ve güçlü başlangıç çözümü.', icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              ) },
              { slug: 'bariyerli-bondex-sunger', title: 'Bariyerli Bondex Sünger', desc: 'Bondex gövde + bariyer katmanı birleşimiyle hava kaynaklı gürültüyü daha etkili azaltır. Duvar, kapı ve tavan uygulamalarında yüksek performans sağlar.', icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              ) },
              { slug: 'cift-bariyerli-bondex-sunger', title: 'Çift Bariyerli Bondex Sünger', desc: 'İki bariyer katmanlı premium yapı sayesinde maksimum izolasyon hedefleyen teknik hacimler ve kritik projeler için en güçlü seçenek.', icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              ) },
            ].map((item, i) => (
              <Link key={item.slug} to={`/${item.slug}`} className={`${styles.bariyerliCard} ${i === 1 ? styles.bariyerliCardWhite : ''} ${styles[`bariyerliCardDelay${i}`]}`}>
                <div className={styles.bariyerliCardIcon}>{item.icon}</div>
                <h3 className={styles.bariyerliCardTitle}>{item.title}</h3>
                <p className={styles.bariyerliCardDesc}>{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section
        ref={(el) => { sectionRefs.current[5] = el; }}
        className={`${styles.section} ${styles.seoContentSection} ${styles.fadeInUp} ${isMobile ? styles.visible : ''}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>Bariyerli Bondex Sünger</div>
            <h2 className={styles.sectionTitle}>Bariyerli Bondex Sünger Hakkında</h2>
            <p className={styles.sectionSubtitle}>
              <strong>Bariyerli Bondex Sünger</strong> özellikleri, avantajları, kullanım alanları ve fiyatları hakkında detaylı bilgiler
            </p>
          </div>
          <div className={styles.seoContent}>
            <article className={styles.seoArticle}>
              <h3>Bariyerli Bondex Sünger Nedir?</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>, ses yalıtımı alanında devrim yaratan kompozit bir yapı malzemesidir.
                Temelinde yüksek yoğunluklu rebonded süngerpan (Bondex Sünger) bulunan bu ürün, üzerine entegre edilen
                ses bariyeri katmanı sayesinde hava kaynaklı gürültüyü etkili bir şekilde minimize eder.
                <strong>Bariyerli Bondex Sünger</strong>, standart Bondex Sünger'in tüm avantajlarını korurken,
                ek bariyer katmanı ile çok daha yüksek ses yalıtım performansı sunar.
              </p>
              <p>
                Modern yapılarda ses yalıtımı artık bir lüks değil, zorunluluk haline gelmiştir. Özellikle şehir
                merkezlerinde artan gürültü kirliliği, konut, ofis ve endüstriyel alanlarda etkili ses izolasyonu
                çözümlerini kaçınılmaz kılmaktadır. <strong>Bariyerli Bondex Sünger</strong>, bu ihtiyaca profesyonel
                düzeyde yanıt veren en etkili çözümlerden biridir.
              </p>

              <h3>Bariyerli Bondex Sünger Teknik Özellikleri</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ürünlerimiz 23mm, 33mm, 43mm ve 53mm kalınlık seçenekleriyle
                sunulmaktadır. Her kalınlık seçeneği, farklı ses yalıtım gereksinimlerine uygun performans sunar:
              </p>
              <ul className={styles.seoList}>
                <li><strong>23mm Bariyerli Bondex Sünger:</strong> Hafif ses izolasyonu gereken alanlar, kapı detayları ve ince duvar uygulamaları için ideal.</li>
                <li><strong>33mm Bariyerli Bondex Sünger:</strong> Orta düzey ses yalıtımı gereken ofisler, toplantı odaları ve yaşam alanları için uygun.</li>
                <li><strong>43mm Bariyerli Bondex Sünger:</strong> Yüksek performans gereken stüdyolar, kayıt odaları ve sinema salonları için tercih edilen seçenek.</li>
                <li><strong>53mm Bariyerli Bondex Sünger:</strong> Maksimum izolasyon gereken endüstriyel alanlar, jeneratör odaları ve teknik hacimler için en güçlü çözüm.</li>
              </ul>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>'in yapısında kullanılan rebonded süngerpan, geri dönüştürülmüş
                sünger parçacıklarının yüksek basınç altında preslenmesiyle elde edilir. Bu sayede yüksek yoğunluk,
                dayanıklılık ve uzun ömür garanti edilir. Bariyer katmanı ise özel formülle üretilen, esnek ve
                ses dalgalarını absorbe eden malzemeden oluşur.
              </p>

              <h3>Bariyerli Bondex Sünger Modelleri</h3>
              <p>
                Echopan üretim hattında <strong>bariyerli süngerpan</strong> ailesine ait dört farklı kalınlık modeli
                bulunur. Her model, Bondex süngerpan gövdesi ile ses bariyeri katmanının fabrika koşullarında
                birleştirilmesiyle üretilir. 23mm model kapı kasası ve ince bölme detaylarında yer kazandırırken
                33mm model konut ve ofis ortak duvarlarında fiyat performans dengesi sunar. 43mm model stüdyo, kayıt
                odası ve yüksek gürültülü makine yakını duvarlarda güçlü sonuç verir. 53mm model ise jeneratör
                odası, kompresör hacmi ve maksimum izolasyon gereken teknik alanlarda tercih edilir.
              </p>
              <p>
                Kalınlık seçimi projenin gürültü seviyesine ve mevcut duvar yapısına göre belirlenir.{' '}
                <strong>Bondex bariyerli ses yalıtım süngeri</strong> olarak her model aynı bariyer kalitesini taşır,
                fark Bondex gövde kalınlığındadır. Daha kalın gövde darbe sesine ve düşük frekanslı titreşime karşı
                ek sönümleme sağlar. <strong>Bariyerli ses yalıtım süngeri</strong> seçiminde yalnızca kalınlığa
                değil, uygulanacak yüzeyin durumuna ve ses kaynağının tipine de bakılmalıdır.{' '}
                <strong>Bariyerli süngerpan fiyatları</strong> kalınlık arttıkça yükselir ancak doğru model ilk
                seferde seçildiğinde gereksiz maliyetten kaçınılır.{' '}
                <strong>Bariyerli bondex ses yalıtım süngeri</strong> modelleri arasında en çok tercih edilen 33mm
                seçeneğidir. Echopan teknik ekibi proje ölçülerinize göre doğru modeli önerir.
              </p>

              <h3>Bariyerli Bondex Sünger ile Bondex Sünger Karşılaştırması</h3>
              <p>
                <strong>Bondex Sünger</strong> ve <strong>Bariyerli Bondex Sünger</strong> arasındaki temel fark,
                bariyer katmanının varlığıdır. Standart Bondex Sünger, rebonded süngerpan yapısıyla ses absorption
                (emilim) ve titreşim sönümleme konusunda etkilidir. Ancak hava kaynaklı ses geçişini tamamen
                engellemek için yeterli olmayabilir.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ise Bondex Sünger'in üzerine eklenen bariyer katmanı
                sayesinde hava kaynaklı ses dalgalarını bloke eder. Bu kombinasyon, hem ses emilimi hem de
                ses izolasyonu sağlayarak çift yönlü koruma sunar. Eğer projenizde yalnızca titreşim sönümleme
                veya hafif akustik düzenleme yeterliyse Bondex Sünger tercih edilebilir. Ancak komşu birim,
                dış ortam veya makine gürültüsünün engellenmesi hedefleniyorsa <strong>Bariyerli Bondex Sünger</strong>
                kesinlikle daha doğru seçimdir.
              </p>

              <h3>Bariyerli Bondex Sünger Kullanım Alanları</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>, geniş bir uygulama yelpazesine sahiptir. Başlıca kullanım alanları şunlardır:
              </p>
              <ul className={styles.seoList}>
                <li><strong>Stüdyo ve Kayıt Odaları:</strong> Profesyonel ses kayıt stüdyoları, podcast odaları ve ev stüdyoları için kritik öneme sahiptir. Dış gürültüyü engellerken iç akustiği optimize eder.</li>
                <li><strong>Ofis ve Toplantı Odaları:</strong> Açık ofis planlarında gizlilik ve konsantrasyon için ses yalıtımı şarttır. <strong>Bariyerli Bondex Sünger</strong>, toplantı odalarında konuşmaların dışarı sızmasını önler.</li>
                <li><strong>Konut Ses Yalıtımı:</strong> Apartmanlarda komşular arası ses geçişi, uyku kalitesi ve yaşam konforunu doğrudan etkiler. Duvar, tavan ve zemin uygulamalarında <strong>Bariyerli Bondex Sünger</strong> sessiz bir yaşam alanı sunar.</li>
                <li><strong>Sinema ve Ev Sinema Odaları:</strong> Film izleme deneyimini optimize etmek için hem ses emilimi hem de izolasyon gereklidir. <strong>Bariyerli Bondex Sünger</strong> bu ikisini bir arada sunar.</li>
                <li><strong>Jeneratör Odaları ve Makine Daireleri:</strong> Endüstriyel gürültü kaynakları çevreye ses kirliği yayar. <strong>Bariyerli Bondex Sünger</strong> bu gürültüyü kaynağında izole eder.</li>
                <li><strong>Kapı ve Duvar Detayları:</strong> Ses zayıf noktalarından sızar. Kapı kasaları, duvar birleşimleri ve pencere çevreleri <strong>Bariyerli Bondex Sünger</strong> ile güçlendirilmelidir.</li>
              </ul>

              <h3>Bariyerli Bondex Sünger Uygulama Yöntemleri</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> uygulaması, yüzey tipine ve projenin gereksinimlerine göre
                değişiklik gösterir. Genel olarak aşağıdaki adımlar takip edilir:
              </p>
              <ol className={styles.seoList}>
                <li><strong>Yüzey Hazırlığı:</strong> Uygulama yapılacak duvar, tavan veya zemin yüzeyi temizlenir ve düzeltilir. Pürüzlü veya nemli yüzeyler uygun primer ile hazırlanır.</li>
                <li><strong>Ölçüm ve Kesim:</strong> <strong>Bariyerli Bondex Sünger</strong> levhaları, uygulama alanına göre ölçülerek kesici aletlerle kesilir. Hassas kesim, minimum fire ve maksimum performans sağlar.</li>
                <li><strong>Yapıştırma:</strong> Uygun yapıştırıcı (neopren bazlı veya PU köpük yapıştırıcı) kullanılarak <strong>Bariyerli Bondex Sünger</strong> yüzeye sabitlenir. Bariyer katmanının yönü, ses kaynağına doğru olmalıdır.</li>
                <li><strong>Birleşim Noktaları:</strong> Levha ek yerleri bantlanır veya köpük sızdırmazlık malzemesiyle kapatılır. Ses sızıntısını önlemek için sürekli bir yüzey oluşturulmalıdır.</li>
                <li><strong>Son Kat Kaplama:</strong> Estetik gereklilikler doğrultusunda <strong>Bariyerli Bondex Sünger</strong> üzerine alçıpan, kumaş kaplama veya akustik paneller monte edilebilir.</li>
              </ol>
              <p>
                Profesyonel uygulama için uzman ekibimizle iletişime geçebilirsiniz. <strong>Bariyerli Bondex Sünger</strong>
                uygulama desteği sunarak projelerinizde en yüksek performansı garanti ediyoruz.
              </p>

              <h3>Bariyerli Bondex Sünger Fiyatları</h3>
              <p>
                <strong>Bariyerli Bondex Sünger fiyatları</strong>, kalınlık, metrekare miktarı ve proje ölçeğine
                göre değişiklik gösterir. 2025 yılı güncel fiyat aralıkları şöyledir:
              </p>
              <ul className={styles.seoList}>
                <li><strong>23mm Bariyerli Bondex Sünger:</strong> 200-230 TL/m²</li>
                <li><strong>33mm Bariyerli Bondex Sünger:</strong> 240-270 TL/m²</li>
                <li><strong>43mm Bariyerli Bondex Sünger:</strong> 280-310 TL/m²</li>
                <li><strong>53mm Bariyerli Bondex Sünger:</strong> 300-330 TL/m²</li>
              </ul>
              <p>
                Karşılaştırma için: Standart <strong>Bondex Sünger</strong> 130-260 TL/m² arasında,
                <strong>Çift Bariyerli Bondex Sünger</strong> ise 350-450 TL/m² arasında fiyatlandırılmaktadır.
                <strong>Bariyerli Bondex Sünger</strong>, performans/fiyat dengesinde en optimal çözümü sunar.
              </p>
              <p>
                Toptan alımlarda ve büyük projelerde özel fiyatlandırma uygulanmaktadır.
                <strong>Bariyerli Bondex Sünger</strong> için en güncel fiyat teklifi almak için WhatsApp üzerinden
                hızlıca iletişime geçebilirsiniz.
              </p>
              <h3>Neden Bariyerli Bondex Sünger Tercih Etmelisiniz?</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>, ses yalıtım pazarında öne çıkan birçok avantaja sahiptir:
              </p>
              <ul className={styles.seoList}>
                <li><strong>Çift Fonksiyon:</strong> Hem ses emilimi hem de ses izolasyonu tek üründe. Ayrı malzeme satın alma ve uygulama maliyetinden tasarruf.</li>
                <li><strong>Kolay Uygulama:</strong> Standart kesici aletlerle kesilebilir, yapıştırıcı ile monte edilir. DIY projelerde bile başarılı sonuçlar alınabilir.</li>
                <li><strong>Yüksek Dayanıklılık:</strong> Rebonded süngerpan yapısı, uzun yıllar performans kaybı olmadan hizmet vermesini sağlar.</li>
                <li><strong>Çevre Dostu:</strong> Geri dönüştürülmüş sünger malzemeden üretilir, sürdürülebilir bir seçimdir.</li>
                <li><strong>Türkiye Geneli Teslimat:</strong> Stoktan hızlı sevkiyat ile projelerinizde gecikme yaşamazsınız.</li>
                <li><strong>Teknik Destek:</strong> Uzman ekibimiz proje analizi, ürün seçimi ve uygulama danışmanlığı konularında yanınızda.</li>
              </ul>

              <h3>Çift Bariyerli Bondex Sünger ile Karşılaştırma</h3>
              <p>
                <strong>Çift Bariyerli Bondex Sünger</strong>, adından da anlaşılacağı üzere çift bariyer katmanı
                içeren premium bir üründür. Maksimum ses izolasyonu gerektiren kritik projelerde tercih edilir.
                Ancak <strong>Bariyerli Bondex Sünger</strong>, çoğu standart proje için yeterli performansı
                daha ekonomik fiyatla sunar.
              </p>
              <p>
                Karar vermekte zorlanıyorsanız, proje detaylarınızı paylaşın. Uzman ekibimiz, ihtiyacınıza en
                uygun ürünü (<strong>Bondex Sünger</strong>, <strong>Bariyerli Bondex Sünger</strong> veya
                <strong>Çift Bariyerli Bondex Sünger</strong>) belirleyerek size özel çözüm sunacaktır.
              </p>

              <h3>Bariyerli Bondex Sünger Sipariş ve Teslimat</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> siparişi vermek oldukça kolaydır. Sepete ekle butonuyla
                ürünleri seçin, kalınlık ve metrekare bilgilerini girin. WhatsApp üzerinden tek tıkla teklif
                isteği gönderin. Uzman ekibimiz en kısa sürede size dönüş yaparak fiyat ve teslimat bilgilerini
                paylaşacaktır.
              </p>
              <p>
                Türkiye genelinde kargo ile teslimat yapılmaktadır. İstanbul, Ankara, İzmir başta olmak üzere
                tüm illere hızlı ve güvenli sevkiyat gerçekleştiriyoruz. Büyük projelerde özel nakliye
                seçenekleri de sunulmaktadır.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ile ses yalıtım projelerinizde profesyonel sonuçlar elde edin.
                Hemen iletişime geçin, projenize özel çözüm ve fiyat teklifi alalım!
              </p>

              <h3>Bariyerli Bondex Sünger Performans Özellikleri</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>'in teknik performansı, ses yalıtım endüstrisinde öne çıkan
                özelliklerinden kaynaklanmaktadır. Ürünün ses yalıtım katsayısı (NRC - Noise Reduction Coefficient),
                bariyer katmanının varlığı sayesinde standart Bondex Sünger'e göre %40-60 daha yüksektir. Bu artış,
                özellikle düşük ve orta frekanslı seslerde belirgin şekilde kendini gösterir.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> aynı zamanda titreşim sönümleme özelliğine de sahiptir.
                Yapısal titreşimlerin yayılmasını engelleyerek, hem hava kaynaklı hem de yapı kaynaklı gürültüyü
                kontrol altına alır. Bu çift yönlü koruma, özellikle apartman daireleri, ofis binaları ve
                endüstriyel tesislerde kritik öneme sahiptir.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ürünlerimiz, yangın güvenliği standartlarına uygun olarak
                üretilmektedir. B1 sınıfı yangın dayanımı ile güvenli kullanım sağlar. Ayrıca nem ve küf direnci
                sayesinde uzun yıllar performans kaybı olmadan hizmet verir.
              </p>

              <h3>Bariyerli Bondex Sünger Bakım ve Dayanıklılık</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>, bakım gerektirmeyen bir üründür. Doğru uygulama ile
                montajlandığında, yıllarca ekstra bakım veya onarım gerektirmez. Rebonded süngerpan yapısı,
                zaman içinde sıkışma veya deformasyon göstermez, bu sayede uzun ömürlü bir yatırım sunar.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ürünlerimiz, UV ışınlarına karşı dayanıklıdır ve
                doğrudan güneş ışığına maruz kaldığında bile performans kaybı yaşamaz. Ancak estetik amaçlı
                kaplama yapılması önerilir. <strong>Bariyerli Bondex Sünger</strong> üzerine alçıpan, kumaş
                kaplama veya akustik paneller monte edilebilir.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>'in temizliği oldukça basittir. Toz birikmesi durumunda
                kuru bezle silinebilir veya hafif nemli bezle temizlenebilir. Kimyasal temizleyici kullanımından
                kaçınılmalıdır. <strong>Bariyerli Bondex Sünger</strong> ürünlerimiz, çevre dostu malzemelerden
                üretildiği için sağlık açısından güvenlidir.
              </p>

              <h3>Bariyerli Bondex Sünger Proje Uygulamaları</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong>, farklı proje tiplerinde başarıyla uygulanmaktadır.
                Konut projelerinde komşu birimler arası ses geçişini engellemek için duvar ve tavan uygulamalarında
                tercih edilir. <strong>Bariyerli Bondex Sünger</strong> ile yapılan izolasyon, yaşam konforunu
                önemli ölçüde artırır.
              </p>
              <p>
                Ticari projelerde <strong>Bariyerli Bondex Sünger</strong>, ofis bölmeleri, toplantı odaları ve
                müşteri hizmet alanlarında kullanılır. Açık ofis planlarında gizlilik sağlamak ve çalışma
                verimliliğini artırmak için <strong>Bariyerli Bondex Sünger</strong> kritik öneme sahiptir.
              </p>
              <p>
                Endüstriyel projelerde <strong>Bariyerli Bondex Sünger</strong>, jeneratör odaları, kompresör
                odaları, HVAC sistem odaları ve üretim hatlarında kullanılır. Makine gürültüsünün çevreye
                yayılmasını engelleyerek, çalışan sağlığını korur ve çevre düzenlemelerine uyum sağlar.
              </p>
              <p>
                Eğitim kurumlarında <strong>Bariyerli Bondex Sünger</strong>, sınıflar, laboratuvarlar ve
                kütüphanelerde kullanılır. Öğrenme ortamını optimize etmek için sessiz bir atmosfer yaratır.
                <strong>Bariyerli Bondex Sünger</strong> ile yapılan akustik düzenlemeler, öğrenci konsantrasyonunu
                artırır ve akademik başarıya katkı sağlar.
              </p>

              <h3>Bariyerli Bondex Sünger Teknik Danışmanlık</h3>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> projelerinizde teknik danışmanlık hizmeti sunuyoruz.
                Uzman ekibimiz, proje analizi yaparak en uygun kalınlık ve uygulama yöntemini belirler.
                <strong>Bariyerli Bondex Sünger</strong> seçiminde, ses kaynağı tipi, frekans aralığı ve hedef
                izolasyon seviyesi dikkate alınır.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> uygulama öncesi, yüzey analizi yapılır ve gerekli
                hazırlık çalışmaları planlanır. Uygulama sırasında teknik destek sağlanır ve kalite kontrolü
                yapılır. <strong>Bariyerli Bondex Sünger</strong> projelerinizde, baştan sona profesyonel
                hizmet garantisi sunuyoruz.
              </p>
              <p>
                <strong>Bariyerli Bondex Sünger</strong> ile ilgili tüm sorularınız için WhatsApp üzerinden
                anında destek alabilirsiniz. Uzman ekibimiz, <strong>Bariyerli Bondex Sünger</strong> hakkında
                detaylı bilgi verir ve projenize özel çözüm önerileri sunar. <strong>Bariyerli Bondex Sünger</strong>
                ile ses yalıtım sorunlarınızı çözüme kavuşturun!
              </p>

              <h2>Bariyerli Bondex ile Birlikte Kullanılan Ses Yalıtım Malzemeleri</h2>
              <p>
                Echopan olarak sahada en sık gördüğümüz hata, tek bir malzemeye her işi yüklemek. Oysa ses
                yalıtımında gerçek sonuç, katmanların doğru sırayla bir araya gelmesinden çıkar.{' '}
                <strong>Bariyerli Bondex Sünger</strong> komşu daireden gelen konuşma, TV sesi veya koridor
                gürültüsü gibi hava kaynaklı problemlerde ana taşıyıcıdır; cam yünü veya taş yünü paneller
                akustik dengeyi tamamlar, ağır bariyerler kritik noktalarda ek fren sağlar. Aşağıda, projelerde
                en çok birlikte kullandığımız malzemeleri ve her birinin ne işe yaradığını net şekilde
                anlatıyoruz.
              </p>

              <h3>Cam Yünü Akustik Panel</h3>
              <p>
                Cam yünü akustik panel, ince duvarlarda hacim kaybını minimumda tutarken ortam içindeki yankıyı
                kontrol etmek için tercih edilir. Yoğunluğu ve yüzey dokusu, orta-yüksek frekanslardaki sesin
                içeride dağılmasını sınırlar. <strong>Bariyerli Bondex Sünger</strong> ile birlikte kullanıldığında
                tipik senaryo şudur: önce bariyerli sünger hava kaynaklı geçişi keser, üzerine veya önüne gelen cam
                yünü katmanı ise kayıt odası, toplantı odası veya ev sinemasında “boğuk” olmayan, dengeli bir ses
                ortamı oluşturur. Özellikle alçıpan karkas sistemlerde cam yünü levha, karkas boşluğunu doldurarak
                titreşim yoluyla taşınan sesi de yumuşatır.
              </p>
              <p>
                Cam yünü tek başına komşu duvar sesini tamamen durdurmaz; bu beklentiyle gidilirse hayal kırıklığı
                kaçınılmazdır. Gücü, <strong>Bariyerli Bondex Sünger uygulaması</strong> ile birlikte düşünüldüğünde
                ortaya çıkar. Echopan projelerinde cam yünü kalınlığını oda hacmine, kullanım amacına ve mevcut
                duvar yapısına göre seçiyoruz; aynı binada stüdyo ile yatak odası için farklı öneriler vermek
                normaldir.
              </p>

              <h3>Taş Yünü Akustik Panel</h3>
              <p>
                Taş yünü akustik panel, yangın dayanımı ve yüksek yoğunluk gereken ticari alanlarda cam yününe
                alternatif veya tamamlayıcı olarak devreye girer. Otel koridorları, hastane teknik hacimleri,
                AVM’deki makine odaları ve endüstriyel tesislerde sık görürüz. Taş yününün sert yapısı, düşük
                frekanslı titreşimlerin yapı elemanına aktarılmasını azaltmaya yardımcı olur; bu da jeneratör,
                kompresör veya klima ünitesi yakınındaki duvarlarda fark yaratır.
              </p>
              <p>
                Konut projelerinde taş yünü genelde daha kalın karkas ve daha yüksek izolasyon hedefi olan
                durumlarda öne çıkar. <strong>Bariyerli Bondex Sünger</strong> bariyer görevini üstlenirken taş
                yünü panel aradaki boşluğu doldurur ve son katmanda alçıpan veya dekoratif kaplama için düzgün bir
                yüzey hazırlanır. İki malzemenin birbirinin yerine geçmesi değil, görev paylaşımı yapması gerekir;
                bu ayrımı net kurduğunuzda hem maliyet hem performans tarafında doğru dengeyi yakalarsınız.
              </p>

              <h3>Ses Bariyeri</h3>
              <p>
                Ses bariyeri, ses dalgasının geçişini fiziksel olarak zorlaştıran ağır ve esnek bir katmandır.
                <strong>Bariyerli Bondex Sünger</strong> zaten içinde bariyer katmanı barındırdığı için birçok
                projede ayrıca ses bariyeri levhası kullanmaya gerek kalmaz. Ancak kapı kasası çevresi, priz
                boşlukları, tavan birleşimleri veya ince bölme duvar gibi zayıf noktalarda ekstra ses bariyeri
                şeridi uygulamak, toplam performansı belirgin şekilde yükseltir.
              </p>
              <p>
                Sahada sık duyduğumuz “bariayeri iki kat kullandık, ses yine geliyor” cümlesinin çoğu zaman nedeni
                malzeme değil, detaydır. Bariyer sürekliliği kopuk olan yerde, üst üste binen katmanlar bile yeterli
                olmaz. Echopan olarak önce zayıf halkayı tespit eder, gerekirse <strong>Bariyerli Bondex Sünger</strong>{' '}
                ana yüzeyde, ince ses bariyeri ise kritik birleşimlerde konumlandırırız. Bu hibrit yaklaşım,
                özellikle mevcut yapıda tadilat yapılan dairelerde işe yarar.
              </p>

              <h3>Keçeli EPDM Ağır Bariyerler</h3>
              <p>
                Keçeli EPDM ağır bariyerler, yüksek kütleli ve esnek yapılarıyla darbe ve düşük frekanslı
                gürültüde güçlüdür. Zemin altı şap uygulamalarında, yüzer döşeme sistemlerinde ve makine
                altlıklarında tercih edilir. Keçe yüzey, alt katmana yapışmayı kolaylaştırırken EPDM gövde titreşimi
                sönümler. <strong>Bariyerli Bondex Sünger</strong> duvar ve tavanda hava kaynaklı sesi hedeflerken,
                keçeli EPDM katmanı ayak sesi, sürüngen ses veya ekipman titreşimini ayrı bir kanaldan keser.
              </p>
              <p>
                Bu ürünler genelde rulo halinde gelir; kesim ve ek yerleri bant veya özel profille kapatılmalıdır.
                Ağır bariyerin üzerine doğrudan sert şap dökülmesi yerine araya uygun ara katman konması, uzun
                vadede çatlamayı ve performans kaybını önler. Kompleks projelerde Echopan, zemin için keçeli EPDM,
                duvar için{' '}
                <Link to="/bariyerli-bondex-sunger" rel="follow" className={styles.leadLink}>
                  Bariyerli Bondex Sünger
                </Link>{' '}
                ve tavan için taş veya cam yünü kombinasyonunu tek çatı altında planlar.
              </p>

              <h3>Akustik Sünger ve Yüzey Kaplamaları</h3>
              <p>
                Stüdyo içinde yankıyı kontrol etmek için bazen ek akustik sünger paneller gerekir; bunlar izolasyon
                değil, absorpsiyon içindir. Dışarıdan ses girmesin diye duvara{' '}
                <strong>Bariyerli Bondex Sünger</strong> uygularsınız; içeride kayıt kalitesi için farklı yoğunlukta
                sünger veya kumaş kaplı panel kullanırsınız. İki ihtiyacı karıştırmak, ya gereksiz harcama ya da
                yetersiz sonuç demektir. Echopan müşterilerine önce “sesin nereden geldiğini” sorar: dış ortam mı,
                iç yankı mı, ikisi birden mi? Cevaba göre malzeme listesi netleşir.
              </p>

              <h3>Bariyerli Bondex Sünger Uygulaması</h3>
              <p>
                <strong>Bariyerli Bondex Sünger uygulaması</strong> her yüzeyde aynı değildir. Betonarme duvara
                yapıştırılan sistem ile metal karkas arasına serilen sistem farklı hazırlık ister. Genel akış
                şöyledir: yüzey temiz ve kuru olmalı, çıkıntılar alınmalı, bariyer katmanı ses kaynağına bakacak
                şekilde yerleştirilmeli, levha ek yerleri üst üste binmeyecek şekilde bindirilmeli ve kenar
                birleşimlerinde süreklilik sağlanmalıdır. Kapı ve pencere çevrelerinde malzeme duvar yüzeyinden
                önce köşeye kadar uzatılmazsa, tüm duvar işi boşa gidebilir.
              </p>
              <p>
                Yapıştırıcı seçimi de kritiktir. Nemli veya tozlu yüzeylerde uygun primer şart; aksi halde levha
                zamanla kabarır. Tavan uygulamasında mekanik destek veya uygun profil kullanımı güvenliği artırır.
                Echopan, metrajı aldıktan sonra mümkünse uygulama fotoğrafları veya basit çizimle detayları netleştirir;
                böylece usta ekipte “hangi yüzeyde bariyer dışarıda kalacak” tartışması yaşanmaz. Profesyonel{' '}
                <strong>Bariyerli Bondex Sünger uygulaması</strong>, malzeme kadar işçilik ve detay işidir.
              </p>

              <h3>Bariyerli Bondex 33 mm En Çok Tercih Edilen Kalınlık</h3>
              <p>
                <strong>Bariyerli Bondex 33 mm</strong>, konut ve ofis projelerinde en dengeli seçeneklerden biridir.
                23 mm’ye göre daha güçlü bariyer etkisi sunar; 43 mm kadar hacim kaybettirmez. Komşu duvar, yatak
                odası ile salon arası bölme veya ev ofisi gibi alanlarda “fiyat–performans–kalınlık” üçlüsünde
                sık önerdiğimiz ölçüdür. Özellikle konuşma ve televizyon sesi gibi günlük gürültüde fark hissedilir;
                disko bası veya endüstriyel makine gürültüsü için ise genelde daha kalın model veya{' '}
                <Link to="/cift-bariyerli-bondex-sunger" rel="follow" className={styles.leadLink}>
                  Çift Bariyerli Bondex
                </Link>{' '}
                değerlendirilir.
              </p>
              <p>
                33 mm levhanın tek başına mucize beklenmemesi gerekir. İnce bölme duvar, boşluklu karkas veya
                kapıdan sızıntı varsa kalınlık artırmak yetmez. Bu yüzden Echopan,{' '}
                <Link to="/bariyerli-bondex-sunger-33mm" rel="follow" className={styles.leadLink}>
                  Bariyerli Bondex 33 mm
                </Link>{' '}
                teklifini verirken duvar kalınlığını, kapı tipini ve varsa mevcut kaplamayı da sorar. Doğru
                senaryoda 33 mm, hem uygulayıcı hem kullanıcı için makul bir çözümdür; yanlış senaryoda 43 mm veya
                sistem revizyonu öneririz — satış için değil, sonuç için.
              </p>

              <h3>Bariyerli Bondex Sünger Fiyatı Nasıl Hesaplanır?</h3>
              <p>
                <strong>Bariyerli Bondex Sünger fiyatı</strong> yalnızca metrekare çarpanından ibaret değildir.
                Kalınlık (23, 33, 43, 53 mm), sipariş metrajı, kesim firesi, teslimat ili ve proje tipi fiyatı
                etkiler. Küçük bir oda ile otel katı aynı m² birim fiyatına sahip olmaz; toplu alımlarda birim
                maliyet düşer, özel kesim ve hızlı sevkiyat talebi artırabilir. Echopan’da WhatsApp üzerinden
                paylaştığınız ölçülerle aynı gün içinde net aralık vermeye çalışıyoruz; böylece bütçe planınızı
                gerçekçi kurarsınız.
              </p>
              <p>
                Fiyat karşılaştırması yaparken sadece levha fiyatına bakmayın. Yanlış kalınlık seçimi, tekrar
                uygulama veya ek malzeme maliyeti getirir. Bazen{' '}
                <Link to="/bondex-sunger" rel="follow" className={styles.leadLink}>
                  Bondex Sünger
                </Link>{' '}
                yeterlidir; bazen 33 mm idealdir; bazen de çift bariyer şarttır. Doğru ürün, toplam maliyeti
                düşürür. <strong>Bariyerli Bondex Sünger fiyatı</strong> için güncel teklif almak istediğinizde
                oda ölçüleri, duvar sayısı ve uygulama yüzeyini (duvar/tavan/zemin) yazmanız yeterli; gerisini
                birlikte netleştiririz.
              </p>

              <h3>Hangi Projede Hangi Kombinasyon?</h3>
              <p>
                Ev dairesi: duvar ve tavan için <strong>Bariyerli Bondex Sünger</strong>, karkas içi cam yünü,
                kapı çevresi ince ses bariyeri. Kayıt stüdyosu: bariyerli sünger + iç absorpsiyon + kapı
                contası detayı. Makine odası: taş yünü + bariyerli sünger + gerekirse keçeli EPDM zemin altı.
                Her proje farklıdır; kopyala-yapıştır liste işe yaramaz. Echopan’ın teknik danışmanlığı tam da
                bu noktada devreye girer: size hazır paket satmak yerine, binanıza ve gürültü kaynağınıza uygun
                katmanları öneririz.
              </p>
              <p>
                Sonuç olarak <strong>Bariyerli Bondex Sünger</strong> tek başına bir çözüm değil, doğru
                kurgulanmış sistemin omurgasıdır. Cam yünü ve taş yünü paneller ortam akustiğini dengeler, ses
                bariyeri kritik detayları kapatır, keçeli EPDM ağır bariyerler titreşim yolunu keser.{' '}
                <strong>Bariyerli Bondex Sünger uygulaması</strong> doğru yapıldığında,{' '}
                <strong>Bariyerli Bondex 33 mm</strong> çoğu konut için tatmin edici seviyede izolasyon sunar ve{' '}
                <strong>Bariyerli Bondex Sünger fiyatı</strong> proje ölçeğine göre şeffaf şekilde hesaplanır.
                Metrajınızı ve hedefinizi paylaşın; Echopan olarak hangi malzemelerin birlikte kullanılacağını
                birlikte belirleyelim.
              </p>

              <h3>Ses Yalıtım Süngeri ve Akustik Çözümler</h3>
              <p>
                <strong>Ses yalıtım süngeri</strong> denildiğinde akla ilk gelen ürünlerden biri{' '}
                <strong>Bariyerli Bondex Sünger</strong>'dir. Modern yapılarda gürültü kontrolü artık bir lüks
                değil, zorunluluk haline gelmiştir. <strong>Akustik sünger</strong> çözümleri arasında öne çıkan
                <strong>Bariyerli Bondex Sünger</strong>, hem konut hem de ticari projelerde tercih edilmektedir.
                <strong>Ses izolasyon malzemesi</strong> olarak rebonded süngerpan ve bariyer katmanının birleşimi,
                hava kaynaklı gürültüyü etkili şekilde engellemektedir.
              </p>
              <p>
                <strong>Duvar ses yalıtımı</strong> projelerinde <strong>Bariyerli Bondex Sünger</strong>, komşu
                birimler arası ses geçişini minimize etmek için ideal bir çözümdür. <strong>Ses yalıtım paneli</strong>{' '}
                alternatiflerine göre daha ekonomik ve kolay uygulanabilir olan <strong>Bariyerli Bondex Sünger</strong>,
                alçıpan veya sıva altına gizlenerek estetik görünümü bozmadan ses izolasyonu sağlar.{' '}
                <strong>Akustik yalıtım malzemesi</strong> olarak stüdyolar, ofisler ve yaşam alanlarında güvenle
                kullanılabilir.
              </p>
              <p>
                <strong>Ses emici sünger</strong> özelliği de taşıyan <strong>Bariyerli Bondex Sünger</strong>,
                hem ses emilimi hem de ses izolasyonu sağlayarak çift yönlü koruma sunar. <strong>Gürültü yalıtım
                  malzemesi</strong> arayan müşterilerimiz için farklı kalınlık seçenekleri sunulmaktadır: 23mm, 33mm,
                43mm ve 53mm. Her kalınlık, farklı <strong>ses yalıtımı</strong> ihtiyaçlarına yanıt vermektedir.
                <strong>Akustik izolasyon</strong> performansı, kalınlık arttıkça yükselmektedir.
              </p>
              <p>
                <strong>Ses yalıtım süngeri fiyatları</strong> kalınlık ve metrekareye göre değişmektedir.{' '}
                <strong>Bariyerli Bondex Sünger</strong> m² fiyatları 200 TL'den başlamakta ve proje ölçeğine göre
                özel fiyatlandırma uygulanmaktadır. <strong>Akustik sünger fiyatları</strong> için en güncel teklifi
                WhatsApp üzerinden alabilirsiniz. <strong>Ses izolasyon süngeri</strong> siparişlerinizde Türkiye
                geneli ücretsiz kargo avantajından yararlanabilirsiniz.
              </p>
              <p>
                <strong>Duvar yalıtım süngeri</strong>, <strong>tavan ses yalıtımı</strong> ve{' '}
                <strong>zemin ses izolasyonu</strong> projelerinizde <strong>Bariyerli Bondex Sünger</strong> ile
                profesyonel sonuçlar elde edin. <strong>Ses geçirmez sünger</strong> arayanlar için bariyer
                katmanlı yapı, maksimum izolasyon performansı sunmaktadır. <strong>Akustik köpük</strong> ve{' '}
                <strong>ses yalıtım köpüğü</strong> alternatiflerine göre daha dayanıklı ve uzun ömürlü olan{' '}
                <strong>Bariyerli Bondex Sünger</strong>, yıllarca performans kaybı olmadan hizmet vermektedir.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section
        className={`${styles.section} ${styles.faqSection} ${styles.fadeInUp} ${styles.visible}`}>
        <div className="container">
          <div className={styles.sectionHeader}>
            <div className={styles.sectionBadge}>SSS</div>
            <h2 className={styles.sectionTitle}>Bariyerli Bondex Sünger Hakkında Sıkça Sorulan Sorular</h2>
            <p className={styles.sectionSubtitle}>
              <strong>Bariyerli Bondex Sünger</strong>, <strong>Bondex Sünger</strong> ve <strong>Çift Bariyerli Bondex Sünger</strong> hakkında merak ettikleriniz
            </p>
          </div>
          <div className={styles.faqCard}>
            <Accordion items={homeFAQs} variant="seo" />
          </div>
        </div>
      </section>

      </div>
    </>
  );
}
